import { createFileRoute, Link } from '@tanstack/react-router';
import { useHistory, clearHistory } from '@/store/history';
import { MethodBadge } from '@/components/playground/MethodBadge';
import { tagSlug, endpointSlug } from '@/lib/slug';
import { useEndpoint } from '@/api/hooks';
import { Trash2, Clock } from 'lucide-react';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/history')({
  component: HistoryPage,
});

function HistoryPage() {
  const history = useHistory();

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
      <div className="flex items-center justify-between mb-8 gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-pill border-2 border-border-strong bg-method-patch shadow-brutal-sm">
            <Clock size={20} />
          </div>
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-2xl md:text-3xl">
              Request history
            </h1>
            <div className="text-2xs font-mono text-fg-tertiary mt-0.5">
              {history.length} request{history.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
        {history.length > 0 && (
          <button
            type="button"
            onClick={() => {
              if (confirm('Clear all history?')) clearHistory();
            }}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-pill border-2 border-border-strong bg-method-delete shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all text-2xs font-display font-bold"
          >
            <Trash2 size={12} />
            Clear all
          </button>
        )}
      </div>

      {history.length === 0 ? (
        <div className="rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal p-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-pill border-2 border-border-strong bg-base mb-4">
            <Clock size={22} className="text-fg-tertiary" />
          </div>
          <p className="font-display font-bold text-base mb-1">Belum ada request.</p>
          <p className="text-sm text-fg-tertiary">
            Fire request lewat <span className="font-mono font-bold">Try it out</span> di endpoint mana pun — muncul di sini.
          </p>
        </div>
      ) : (
        <div className="rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal-sm divide-y-2 divide-border-strong overflow-hidden">
          {history.map((h) => (
            <HistoryRow key={h.id} entry={h} />
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ entry }: { entry: ReturnType<typeof useHistory>[number] }) {
  const ep = useEndpoint(entry.operationId);
  const linkParams = ep
    ? { tag: tagSlug(ep.tag), operation: endpointSlug(ep.operationId) }
    : null;

  const time = new Date(entry.timestamp);
  const ago = relativeTime(entry.timestamp);

  return (
    <div className="px-4 py-3 flex items-center gap-3 hover:bg-overlay transition-colors">
      <MethodBadge method={entry.method} />
      {linkParams ? (
        <Link
          to="/endpoints/$tag/$operation"
          params={linkParams}
          className="font-mono text-2xs font-bold text-fg hover:underline truncate flex-1"
        >
          {entry.path}
        </Link>
      ) : (
        <span className="font-mono text-2xs font-bold text-fg truncate flex-1">{entry.path}</span>
      )}
      <span
        className={cn(
          'inline-flex items-center px-2 h-6 rounded-pill border-2 border-border-strong font-mono text-2xs font-bold',
          entry.ok ? 'bg-method-get' : 'bg-method-delete'
        )}
      >
        {entry.status}
      </span>
      <span className="text-2xs text-fg-tertiary font-mono shrink-0">
        {entry.durationMs.toFixed(0)}ms
      </span>
      <span
        className="text-2xs text-fg-tertiary shrink-0 w-20 text-right"
        title={time.toLocaleString()}
      >
        {ago}
      </span>
    </div>
  );
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}
